import { DiagramConnectorLabel } from "@/app/features/diagrams/components/shared/DiagramConnectorLabel";
import {
  type DiagramConnector,
  type DiagramTopologySpec,
  validateDiagramTopology,
} from "@/app/features/diagrams/functions/diagramTypes";

type StateMachineFlowTone =
  "neutral" | "agent" | "repair" | "success" | "danger";

type StateMachineConnector = DiagramConnector & {
  path: string;
  tone: StateMachineFlowTone;
  strokeWidth: number;
  opacity?: number;
  label?: { x: number; y: number; text: string };
};

const toneStyles: Record<
  StateMachineFlowTone,
  { stroke: string; marker: string }
> = {
  neutral: { stroke: "currentColor", marker: "arrow-neutral" },
  agent: { stroke: "var(--ga-diagram-agent)", marker: "arrow-blue" },
  repair: { stroke: "var(--ga-diagram-repair)", marker: "arrow-repair" },
  success: { stroke: "var(--ga-diagram-success)", marker: "arrow-green" },
  danger: { stroke: "var(--ga-diagram-danger)", marker: "arrow-fallback" },
};

const stateMachineConnectors: StateMachineConnector[] = [
  {
    id: "start",
    source: { nodeId: "entry", anchor: "right-center" },
    target: { nodeId: "idle", anchor: "left-center" },
    meaning: "Pipeline entry enters the idle state",
    semanticType: "control",
    lineStyle: "solid",
    path: "M 44 240 L 67 240",
    tone: "neutral",
    strokeWidth: 1.8,
  },
  {
    id: "input",
    source: { nodeId: "idle", anchor: "right-center" },
    target: { nodeId: "extract", anchor: "left-center" },
    meaning: "Input enters extraction",
    semanticType: "control",
    lineStyle: "solid",
    path: "M 144 240 L 193 240",
    tone: "neutral",
    strokeWidth: 1.8,
    opacity: 0.6,
    label: { x: 169, y: 209, text: "[input]" },
  },
  {
    id: "payload",
    source: { nodeId: "extract", anchor: "right-center" },
    target: { nodeId: "validate", anchor: "left-center" },
    meaning: "LLM output enters validation",
    semanticType: "probabilistic",
    lineStyle: "dashed",
    path: "M 290 240 L 387 240",
    tone: "agent",
    strokeWidth: 1.8,
    label: { x: 340, y: 209, text: "[payload]" },
  },
  {
    id: "repair",
    source: { nodeId: "validate", anchor: "top-center" },
    target: { nodeId: "repair", anchor: "bottom-center" },
    meaning: "Recoverable validation error invokes repair",
    semanticType: "feedback",
    lineStyle: "solid",
    path: "M 414 188 L 414 133",
    tone: "repair",
    strokeWidth: 1.8,
    label: { x: 340, y: 164, text: "[err & retries < 3]" },
  },
  {
    id: "repaired",
    source: { nodeId: "repair", anchor: "bottom-center" },
    target: { nodeId: "validate", anchor: "top-center" },
    meaning: "Repaired output returns for validation",
    semanticType: "feedback",
    lineStyle: "solid",
    path: "M 470 133 L 470 186",
    tone: "repair",
    strokeWidth: 1.8,
    label: { x: 521, y: 164, text: "[repaired]" },
  },
  {
    id: "commit",
    source: { nodeId: "validate", anchor: "right-center" },
    target: { nodeId: "commit", anchor: "left-center" },
    meaning: "Validated invariants permit commit",
    semanticType: "success",
    lineStyle: "solid",
    path: "M 494 240 L 588 240",
    tone: "success",
    strokeWidth: 2,
    label: { x: 541, y: 209, text: "[invariants ok]" },
  },
  {
    id: "persist",
    source: { nodeId: "commit", anchor: "bottom-center" },
    target: { nodeId: "done", anchor: "top-center" },
    meaning: "Commit persists successfully",
    semanticType: "success",
    lineStyle: "solid",
    path: "M 636 286 L 636 352",
    tone: "success",
    strokeWidth: 2,
    label: { x: 686, y: 324, text: "[persisted]" },
  },
  {
    id: "escalate",
    source: { nodeId: "validate", anchor: "bottom-center" },
    target: { nodeId: "escalate", anchor: "top-center" },
    meaning: "Fatal or exhausted retries escalate to human operations",
    semanticType: "failure",
    lineStyle: "dashed",
    path: "M 442 292 L 442 352",
    tone: "danger",
    strokeWidth: 1.8,
    label: { x: 533, y: 324, text: "[retries ≥ 3 / fatal]" },
  },
];

const topology: DiagramTopologySpec = {
  nodes: [
    "entry",
    "idle",
    "extract",
    "validate",
    "repair",
    "commit",
    "done",
    "escalate",
  ],
  junctions: [],
  connectors: stateMachineConnectors,
};

if (process.env.NODE_ENV !== "production") validateDiagramTopology(topology);

export function StateMachineFlows() {
  return (
    <>
      <circle cx="36" cy="240" r="8" fill="currentColor" opacity="0.85" />
      {stateMachineConnectors.map((connector) => {
        const style = toneStyles[connector.tone];

        return (
          <g key={connector.id}>
            <path
              d={connector.path}
              fill="none"
              stroke={style.stroke}
              strokeWidth={connector.strokeWidth}
              strokeDasharray={
                connector.lineStyle === "dashed" ? "5 3" : undefined
              }
              opacity={connector.opacity}
              markerEnd={`url(#smv-${style.marker})`}
            />
            {connector.label && (
              <DiagramConnectorLabel {...connector.label} fill={style.stroke} />
            )}
          </g>
        );
      })}
    </>
  );
}
